import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';

import { AccountService } from '../../services/account.service';

/** One address an author can link to, as offered in the picker. */
export interface TargetCandidate {
  /** The address itself — what goes in `target`. */
  path: string;
  /** Page title, offered as the link label when the author has not typed one. */
  title: string;
  /** Grouping shown in the dropdown ("Pages", "Regions", …). */
  group: string;
  /** Unpublished pages stay pickable but say so. */
  note?: string;
}

/**
 * Region-level service pages exist at /{state}/{region}/{service} for EVERY
 * region. The segments are picaluna-v3 constants (CANONICAL_REGION_SERVICES in
 * its src/lib/urls.ts), not Conductor records, so they are mirrored here.
 *
 * MIRRORED LIST — the source of truth is CANONICAL_REGION_SERVICES in
 * picaluna-v3/src/lib/urls.ts, which carries the matching warning. Changing
 * v3's list without changing this one makes the picker offer addresses that
 * 404, which is the exact bug #199 is meant to prevent. The cost of the mirror
 * is accepted because the alternative is a new endpoint for five strings that
 * have not changed since launch.
 *
 * Note these are NOT the account's `siteServices` records: those have no slug
 * and do not map to region URLs at all.
 */
export const REGION_SERVICE_SEGMENTS: Array<{ segment: string; label: string }> = [
  { segment: 'burial', label: 'Burial' },
  { segment: 'cremations', label: 'Cremation' },
  { segment: 'venues', label: 'Funeral venues' },
  { segment: 'funeral-cost', label: 'Funeral costs' },
  { segment: 'cremation-costs', label: 'Cremation costs' },
];

/**
 * The service segment that takes a SUBURB beneath it
 * (/{state}/{region}/funeral-services/{suburb}) — SUBURB_PAGE_SERVICE in v3's
 * suburb-services.mjs. One page per suburb (#257): the live site's
 * funeral-directors, direct-cremation and memorial-services addresses only
 * redirect now, so a typed one is flagged like any other address with no page.
 *
 * Those pages are not offered as candidates (several hundred is not a list
 * anyone scrolls), but the shape is needed so a hand-typed suburb address is
 * not wrongly flagged as unknown. Same mirroring caveat as above.
 */
export const SUBURB_SERVICE_SEGMENTS = ['funeral-services'];

/**
 * The addresses picaluna-v3 will actually build, for the link/button pickers
 * (#199). Free-text targets are still the contract — this only removes the
 * need to TYPE the common ones, where a typo passes `safeTarget` validation
 * and publishes a 404 to a family.
 *
 * Page candidates come from the account's own content records, which the
 * editor already holds. That is deliberate: the alternative is a new endpoint
 * mirroring v3's route table, and a mirror drifts.
 *
 * WHAT THIS CANNOT SEE, and why it still works:
 * v3's reserved-route set is derived at BUILD time from `import.meta.glob`
 * over src/pages (see [...page].astro), so file routes are invisible from
 * here. In practice the seeded sitemap registered those addresses as locked
 * CMS pages, so `content.pages` already covers all but a couple of them — and
 * any it misses stay typeable. This is why an unmatched path WARNS rather than
 * blocks: this list is the common case, not the whole truth.
 *
 * Suburb pages (/{state}/{region}/{service}/{suburb}) are deliberately absent:
 * they run to several hundred, which is a list nobody scrolls. Regions and
 * region services are ~50 together, which is browsable, so they are in.
 */
/**
 * What the picker offers, plus what it merely RECOGNISES. Suburb pages are in
 * the second group: too many to list, but they must not be flagged as typos.
 */
export interface TargetKnowledge {
  candidates: TargetCandidate[];
  /** "/nsw/sydney" → the suburb slugs that region actually has. */
  suburbsByRegionPath: Map<string, Set<string>>;
  /**
   * False until the content subtrees are actually loaded. The picker must not
   * warn "no page at this address" while it is still true — it would flag
   * every correct address on the form.
   */
  loaded: boolean;
}

@Injectable({ providedIn: 'root' })
export class TargetCandidatesService {
  private cache$?: Observable<TargetKnowledge>;

  constructor(private accountService: AccountService) {}

  /**
   * Cached for the life of the session: an author opening six sections should
   * not re-derive this six times, and content records do not change under an
   * open editor in a way that matters to a link picker.
   */
  knowledge$(): Observable<TargetKnowledge> {
    if (!this.cache$) {
      // ensureFullAccount() FIRST, and account$ only after it resolves.
      //
      // account$ replays a cached, content-STRIPPED account instantly — the
      // server omits the content subtrees from ordinary account GETs. Reading
      // it directly gave an empty candidate list, which made the picker warn
      // "no page at this address" against every correct address on the form
      // (/contact, reported on prod). Every other content component gates on
      // ensureFullAccount for the same reason.
      this.cache$ = this.accountService.ensureFullAccount().pipe(
        switchMap(() => this.accountService.account$),
        map((a: any) => this.derive(a)),
        catchError(() =>
          of({
            candidates: [],
            suburbsByRegionPath: new Map(),
            loaded: false,
          } as TargetKnowledge)
        ),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.cache$;
  }

  /** Content records are stored as id-keyed maps, but arrive as arrays too. */
  private list(raw: any): any[] {
    if (!raw) return [];
    const items = Array.isArray(raw) ? raw : Object.values(raw);
    return items.filter((x: any) => x && typeof x === 'object');
  }

  private derive(account: any): TargetKnowledge {
    const content = account?.data?.content;
    const suburbsByRegionPath = new Map<string, Set<string>>();
    // No content subtree = not loaded, NOT "the site has no pages".
    if (!content) return { candidates: [], suburbsByRegionPath, loaded: false };

    const out: TargetCandidate[] = [];

    // ── CMS pages ────────────────────────────────────────────────────────
    for (const p of this.list(content.pages)) {
      if (p?._meta?.deleted) continue;
      const path = this.normalise(p.path);
      if (!path) continue;
      out.push({
        path,
        title: String(p.title || path),
        group: 'Pages',
        // Linking to an unpublished page is legitimate (it may go live in the
        // same release), so it is offered — labelled, not withheld.
        note: p.published === false ? 'not published yet' : undefined,
      });
    }

    // ── Region hubs, and the canonical services under each ───────────────
    const regions = this.list(content.regions).filter((r: any) => r.slug);
    const suburbsByRegionId = new Map<string, Set<string>>();
    for (const sb of this.list(content.suburbs)) {
      // A removed suburb's page is gone, so a link to it SHOULD warn.
      if (!sb.slug || !sb.regionId || sb?._meta?.deleted) continue;
      const set = suburbsByRegionId.get(sb.regionId) ?? new Set<string>();
      set.add(String(sb.slug));
      suburbsByRegionId.set(sb.regionId, set);
    }

    for (const r of regions) {
      const state = String(r.state || 'NSW').toLowerCase();
      const hub = `/${state}/${r.slug}`;
      const regionName = String(r.name || r.slug);
      // v3 builds a hub and its service pages for PUBLISHED regions only, so
      // an unpublished region's addresses do not exist yet. Offered anyway —
      // the region may go live in the same release — but labelled, exactly as
      // an unpublished page is.
      const note = r.published === false ? 'region not published yet' : undefined;

      out.push({ path: hub, title: regionName, group: 'Regions', note });

      for (const s of REGION_SERVICE_SEGMENTS) {
        out.push({
          path: `${hub}/${s.segment}`,
          title: `${s.label} — ${regionName}`,
          group: 'Services by region',
          note,
        });
      }

      const subs = suburbsByRegionId.get(r?._meta?.id);
      if (subs?.size) suburbsByRegionPath.set(hub, subs);
    }

    // A region hub can reach this list twice, and the two sources are NOT
    // equivalent:
    //
    //   - The Regions panel owns the address. Every published region has a
    //     hub at /{state}/{slug} whether or not anyone has designed it.
    //   - A `pages` record exists only once someone adds sections to that
    //     region ("a designed page") — the region editor upserts it on save
    //     and REMOVES it when the sections are emptied.
    //
    // So pages cannot be the source for hubs: an undesigned region would be
    // missing from the picker and then flagged as a typo when typed. Both are
    // emitted and the duplicate dropped here, keeping the page record because
    // it carries the author's own title. Today every region happens to be
    // designed, which makes the Regions group look redundant — it is not.
    const seen = new Set<string>();
    const unique = out.filter((c) => (seen.has(c.path) ? false : (seen.add(c.path), true)));

    return {
      candidates: unique.sort((a, b) => a.path.localeCompare(b.path)),
      suburbsByRegionPath,
      loaded: true,
    };
  }

  /** "/faq/" and "faq" both mean /faq; "" and "/" both mean the home page. */
  private normalise(raw: unknown): string {
    const s = String(raw ?? '').trim();
    if (!s) return '';
    const withSlash = s.startsWith('/') ? s : `/${s}`;
    return withSlash.replace(/\/+$/, '') || '/';
  }
}
